import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  getSettleData,
  setSettleData,
  getTTFStatus,
} from "../../services/user.service";
import { dataByObkey } from "../../common/extra";
import { customSort, getCurve, compareArrays } from "../../utils/helpers";
import { TTF_STATUS_POLL_INTERVAL_MS } from "../../utils/constants";
import {
  CONCURRENT_MODIFICATION_MSG,
  DEFAULT_SAVE_ERROR_MSG,
  MARKET_PRICE_EXISTS_MSG,
  REASON_CONCURRENT_MODIFICATION,
  REASON_MARKET_PRICE_EXISTS,
  REASON_RECALC_IN_PROGRESS,
  REASON_TTF_RECALCULATED,
  RECALC_IN_PROGRESS_MSG,
  TTF_RECALCULATED_MSG,
} from "../../utils/constants/last-price";

const DEFAULT_SAVE_SUCCESS_MSG = "Precios guardados correctamente.";

const useLastPrice = () => {
  const [data, setData] = useState([]);
  const [origdata, setOrigData] = useState([]);
  const [databyCurve, setDatabyCurve] = useState(new Map());
  const [startDate, setStartDate] = useState(new Date());
  const [customSettles, setcustomSettles] = useState({});
  const [dropdownValue, setDropdownValue] = useState({});
  const [ttfStatus, setTtfStatus] = useState(null);
  const [fromMibgenerics, setFromMibgenerics] = useState(false);
  const [editStartedAt, setEditStartedAt] = useState(new Date().toISOString());
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [focusedEditableIndex, setFocusedEditableIndex] = useState(null);

  const isEditingLocked = Boolean(ttfStatus?.recalcInProgress);

  const buildCurveData = useCallback((rows) => {
    rows.forEach((aux) => {
      aux.curve = getCurve(aux.instrument);
    });
    const lpbyCurve = dataByObkey(rows, "curve");
    lpbyCurve.forEach((valueList) => {
      valueList.forEach((dict) => {
        delete dict["curve"];
      });
    });
    return lpbyCurve;
  }, []);

  const refresh = useCallback(
    (mydate) => {
      getSettleData(mydate)
        .then((res) => {
          setcustomSettles({});
          setDropdownValue({});
          setFocusedEditableIndex(null);
          const {
            rows,
            fromMibgenerics: isMibgenerics,
            error: apiError,
          } = res.data;
          if (apiError === "no_mibgenerics_data") {
            console.warn("[useLastPrice] mibgenerics vacío para", mydate);
            toast.warn(
              "No hay instrumentos disponibles en mibgenerics para esta fecha."
            );
          }
          setFromMibgenerics(Boolean(isMibgenerics));
          const sortedData = (rows || res.data).sort((a, b) =>
            customSort(a.instrument, b.instrument)
          );
          setData(sortedData);
          setOrigData(sortedData.map((item) => Object.assign({}, item)));
          setDatabyCurve(buildCurveData(sortedData));
          setEditStartedAt(new Date().toISOString());
        })
        .catch((err) => {
          console.error("[useLastPrice] Error al cargar datos:", err);
          toast.error(
            `Error al cargar datos: ${err?.response?.data?.message ?? err.message}`
          );
        });
    },
    [buildCurveData]
  );

  const fetchTtfStatus = useCallback(async () => {
    if (!startDate) return;
    try {
      const res = await getTTFStatus(startDate);
      setTtfStatus(res.data);
    } catch (err) {
      console.log(err);
    }
  }, [startDate]);

  const batchsendDataToBackend = async (updaterows) => {
    setIsSaving(true);
    try {
      const dateStr = startDate.toISOString().split("T")[0];
      const response = await setSettleData({
        rows: updaterows,
        editStartedAt,
        date: dateStr,
      });
      const {
        rows: returnedRows,
        fromMibgenerics: isMibgenerics,
        message: successMessage,
      } = response.data;
      if (returnedRows && returnedRows.length > 0) {
        // El backend devuelve la tabla completa: filas guardadas + filas vacías del resto
        // de instrumentos de mibgenerics que el usuario aún no ha rellenado.
        const sortedData = returnedRows.sort((a, b) =>
          customSort(a.instrument, b.instrument)
        );
        setcustomSettles({});
        setDropdownValue({});
        setFocusedEditableIndex(null);
        setFromMibgenerics(Boolean(isMibgenerics));
        setData(sortedData);
        setOrigData(sortedData.map((item) => Object.assign({}, item)));
        setDatabyCurve(buildCurveData(sortedData));
        setEditStartedAt(new Date().toISOString());
      } else {
        setFocusedEditableIndex(null);
        refresh(startDate);
      }
      toast.success(successMessage || DEFAULT_SAVE_SUCCESS_MSG);
    } catch (error) {
      const reason = error.response?.data?.reason;
      const message = error.response?.data?.message || DEFAULT_SAVE_ERROR_MSG;

      if (reason === REASON_RECALC_IN_PROGRESS) {
        toast.error(RECALC_IN_PROGRESS_MSG);
        refresh(startDate);
      } else if (reason === REASON_TTF_RECALCULATED) {
        toast.error(TTF_RECALCULATED_MSG);
        refresh(startDate);
      } else if (reason === REASON_CONCURRENT_MODIFICATION) {
        toast.error(CONCURRENT_MODIFICATION_MSG);
        refresh(startDate);
      } else if (reason === REASON_MARKET_PRICE_EXISTS) {
        const instruments = error.response?.data?.instruments ?? [];
        const detail =
          instruments.length > 0
            ? ` Instrumentos afectados: ${instruments.join(", ")}.`
            : "";
        toast.error(MARKET_PRICE_EXISTS_MSG + detail);
        refresh(startDate);
      } else {
        toast.error(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const updateNewData = (newData, ind, params, settle) => {
    if (params.hasOwnProperty("bid")) {
      newData[ind]["settle_bid"] = newData[ind][params["bid"]];
    } else {
      newData[ind]["settle_bid"] = null;
    }

    if (params.hasOwnProperty("ask")) {
      newData[ind]["settle_ask"] = newData[ind][params["ask"]];
    } else {
      newData[ind]["settle_ask"] = null;
    }

    if (settle != null && params.hasOwnProperty("quality")) {
      newData[ind]["settle_quality_factor"] = params["quality"];
    } else {
      newData[ind]["settle_quality_factor"] = null;
    }
    newData[ind]["settle"] = settle;
    return newData;
  };

  const handleDropdownChange = useCallback(
    (event, index) => {
      if (isEditingLocked) return;
      const modifer = {
        settle_price1: { bid: "settle_bid1", ask: "settle_ask1", quality: 1 },
        settle_price2: { bid: "settle_bid2", ask: "settle_ask2", quality: 2 },
        settle_price3: { bid: "settle_bid3", ask: "settle_ask3", quality: 3 },
        settle_tullet: { quality: 4 },
        settle_omip: { quality: 5 },
        custom: { quality: 20 },
        anticipated: { quality: 7 },
        remove: "",
      };

      let newDrop = { ...dropdownValue };
      let newData = [...data];
      let targetVal = event.target.value;
      let params = modifer[targetVal];

      if (index !== -1) {
        newDrop[index] = targetVal;
      }

      if (targetVal === "custom" || targetVal === "anticipated") {
        setFocusedEditableIndex(index);
      } else if (index !== -1 && focusedEditableIndex === index) {
        setFocusedEditableIndex(null);
      }

      setDropdownValue(newDrop);

      if (targetVal === "custom" || targetVal === "anticipated") {
        const nextCustomSettles = { ...customSettles };
        if (
          targetVal === "anticipated" &&
          index !== -1 &&
          !nextCustomSettles.hasOwnProperty(index) &&
          newData[index]?.settle_modified != null
        ) {
          nextCustomSettles[index] = newData[index].settle_modified;
        }

        setcustomSettles(nextCustomSettles);
        Object.keys(customSettles).forEach((ind) => {
          const indDropVal = newDrop[ind] || dropdownValue[ind];
          const indParams = modifer[indDropVal] || params;
          newData = updateNewData(newData, ind, indParams, customSettles[ind]);
        });

        if (nextCustomSettles.hasOwnProperty(index)) {
          newData = updateNewData(
            newData,
            index,
            params,
            nextCustomSettles[index]
          );
        }
      } else {
        if (targetVal === "remove") {
          newData = updateNewData(newData, index, params, null);
        } else {
          newData = updateNewData(
            newData,
            index,
            params,
            newData[index][targetVal]
          );
        }
        if (customSettles.hasOwnProperty(index)) {
          const nextCustom = { ...customSettles };
          delete nextCustom[index];
          setcustomSettles(nextCustom);
        }
      }

      setData(newData);
      setDatabyCurve(buildCurveData(newData));
    },
    [
      dropdownValue,
      data,
      customSettles,
      focusedEditableIndex,
      isEditingLocked,
      buildCurveData,
    ]
  );

  const SETTLE_MIN = 0;
  const SETTLE_MAX = 1000;

  const handleInputChange = (event, index) => {
    if (isEditingLocked) return;
    let newCustom = { ...customSettles };
    let candidate = event.target.value;
    candidate = candidate.replace(",", ".");
    candidate = parseFloat(candidate);
    newCustom[index] = isNaN(candidate) ? null : candidate;
    setcustomSettles(newCustom);
  };

  const buildValueUpdater = () => {
    const valueUpdater = compareArrays(origdata, data);
    valueUpdater.forEach((row) => {
      const orig = origdata.find(
        (o) => o.dtime === row.dtime && o.instrument === row.instrument
      );
      row.original_settle_last_modification =
        orig?.settle_last_modification ?? null;
    });
    return valueUpdater;
  };

  const confirmSave = () => {
    if (!pendingConfirm) return;
    const { valueUpdater } = pendingConfirm;
    setPendingConfirm(null);
    batchsendDataToBackend(valueUpdater);
  };

  const cancelSave = () => setPendingConfirm(null);

  const handleClick = () => {
    if (isEditingLocked) return;

    // Validación: en modo precios anticipados (fromMibgenerics), el contrato M+1 (GMAES)
    // es el ancla mínima obligatoria para que el algoritmo de completación de curva funcione.
    if (fromMibgenerics) {
      const anticipatedIndices = Object.keys(dropdownValue).filter(
        (i) => dropdownValue[i] === "anticipated" && customSettles[i] != null
      );

      // Validar que todos los precios anticipados son números válidos en rango
      const invalidPrices = anticipatedIndices.filter((i) => {
        const v = customSettles[i];
        return v === null || !isFinite(v) || v <= SETTLE_MIN || v > SETTLE_MAX;
      });
      if (invalidPrices.length > 0) {
        const instruments = invalidPrices.map(
          (i) => data[i]?.instrument ?? `fila ${i}`
        );
        toast.error(
          `Precios inválidos o fuera de rango (0–${SETTLE_MAX} €/MWh) en: ${instruments.join(", ")}. Corrígelos antes de guardar.`
        );
        return;
      }
      if (anticipatedIndices.length > 0) {
        const mplus1Index = data.findIndex((r) =>
          r.instrument?.startsWith("GMAES_")
        );
        const mplus1IsSet =
          mplus1Index !== -1 &&
          dropdownValue[mplus1Index] === "anticipated" &&
          customSettles[mplus1Index] != null;
        if (!mplus1IsSet) {
          toast.error(
            "El contrato M+1 (GMAES) es obligatorio para completar la curva PVB. " +
              "Introduce un precio anticipado para ese instrumento antes de guardar."
          );
          return;
        }

        // Modal de confirmación si faltan Q+1 o Y+1
        const missingLabels = [];
        const qplus1Index = data.findIndex((r) =>
          r.instrument?.startsWith("GQES_")
        );
        const qplus1IsSet =
          qplus1Index !== -1 &&
          dropdownValue[qplus1Index] === "anticipated" &&
          customSettles[qplus1Index] != null;
        if (!qplus1IsSet) missingLabels.push("Q+1 (GQES_Q+1)");

        const yplus1Index = data.findIndex((r) =>
          r.instrument?.startsWith("GYES_")
        );
        const yplus1IsSet =
          yplus1Index !== -1 &&
          dropdownValue[yplus1Index] === "anticipated" &&
          customSettles[yplus1Index] != null;
        if (!yplus1IsSet) missingLabels.push("Y+1 (GYES_Y+1)");

        if (missingLabels.length > 0) {
          setPendingConfirm({
            missingLabels,
            valueUpdater: buildValueUpdater(),
          });
          return;
        }
      }
    }

    batchsendDataToBackend(buildValueUpdater());
  };

  // Debounce custom settles → apply input values after 1s
  useEffect(() => {
    let timeoutId;
    if (Object.keys(customSettles).length !== 0) {
      // Determinar qué tipo de precio aplicar según el dropdown pendiente
      const firstPendingIdx = Object.keys(customSettles)[0];
      const dv = dropdownValue[firstPendingIdx];
      const applyVal = dv === "anticipated" ? "anticipated" : "custom";
      timeoutId = setTimeout(() => {
        handleDropdownChange({ target: { value: applyVal } }, -1);
      }, 1000);
    }
    return () => clearTimeout(timeoutId);
  }, [customSettles, handleDropdownChange, dropdownValue]);

  // Refresh data when date changes
  useEffect(() => {
    refresh(startDate);
  }, [startDate, refresh]);

  // Poll TTF status
  useEffect(() => {
    fetchTtfStatus();
    const intervalId = setInterval(fetchTtfStatus, TTF_STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchTtfStatus]);

  // Clear local changes when editing becomes locked
  useEffect(() => {
    if (!isEditingLocked) return;
    const hasLocalChanges =
      Object.keys(customSettles).length > 0 ||
      Object.keys(dropdownValue).length > 0 ||
      compareArrays(origdata, data).length > 0;

    if (hasLocalChanges) {
      setcustomSettles({});
      setDropdownValue({});
      setFocusedEditableIndex(null);
      refresh(startDate);
    }
  }, [
    isEditingLocked,
    customSettles,
    dropdownValue,
    origdata,
    data,
    startDate,
    refresh,
  ]);

  const hasChanges = useMemo(
    () => compareArrays(origdata, data).length > 0,
    [origdata, data]
  );

  return {
    data,
    databyCurve,
    startDate,
    setStartDate,
    customSettles,
    dropdownValue,
    isEditingLocked,
    fromMibgenerics,
    hasChanges,
    isSaving,
    handleDropdownChange,
    handleInputChange,
    handleClick,
    refresh,
    pendingConfirm,
    confirmSave,
    cancelSave,
    focusedEditableIndex,
  };
};

export default useLastPrice;
